import PropTypes from "prop-types";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";

import { settings } from "config";
import { getColor, getItemFromStore } from "helpers/utils";
import useToggleStyle from "hooks/useToggleStyle";
import { configReducer } from "reducers/configReducer";

// Module-level stable reference — never recreated across renders
const getThemeColor = (name) => getColor(name);

// Module-level constant — stable forever
const STORE_KEYS = [
  "isFluid",
  "isRTL",
  "isDark",
  "theme",
  "navbarPosition",
  "isNavbarVerticalCollapsed",
  "navbarStyle",
];

export const AppConfigContext = createContext(settings);
export const AppActionsContext = createContext({});

// Backward-compat alias so any code that imports AppContext directly still works
export const AppContext = AppConfigContext;

const AppProvider = ({ children }) => {
  const storedTheme = getItemFromStore("theme", settings.theme);
  const computedIsDark =
    storedTheme === "auto"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
      : storedTheme === "dark";

  const configState = {
    isFluid: getItemFromStore("isFluid", settings.isFluid),
    isRTL: getItemFromStore("isRTL", settings.isRTL),
    isDark: computedIsDark,
    theme: storedTheme,
    navbarPosition: getItemFromStore("navbarPosition", settings.navbarPosition),
    disabledNavbarPosition: [],
    isNavbarVerticalCollapsed: getItemFromStore(
      "isNavbarVerticalCollapsed",
      settings.isNavbarVerticalCollapsed
    ),
    navbarStyle: getItemFromStore("navbarStyle", settings.navbarStyle),
    currency: settings.currency,
    showBurgerMenu: settings.showBurgerMenu,
    showSettingPanel: false,
    navbarCollapsed: false,
    openAuthModal: false,
  };

  const [config, configDispatch] = useReducer(configReducer, configState);

  // configDispatch from useReducer is guaranteed stable by React
  const setConfig = useCallback(
    (key, value) => {
      configDispatch({
        type: "SET_CONFIG",
        payload: { key, value, setInStore: STORE_KEYS.includes(key) },
      });
    },
    [configDispatch]
  );

  const changeTheme = useCallback(
    (theme) => {
      const isDark =
        theme === "auto"
          ? window.matchMedia("(prefers-color-scheme: dark)").matches
          : theme === "dark";
      document.documentElement.setAttribute(
        "data-bs-theme",
        isDark ? "dark" : "light"
      );
      configDispatch({ type: "SET_THEME", payload: { theme, isDark } });
    },
    [configDispatch]
  );

  const { isLoaded } = useToggleStyle(config.isRTL, config.isDark);

  // actionsContextValue is stable across renders (all members are stable)
  const actionsContextValue = useMemo(
    () => ({ setConfig, configDispatch, changeTheme, getThemeColor }),
    [setConfig, changeTheme, configDispatch]
  );

  // configContextValue only changes on real user actions that alter config
  const configContextValue = useMemo(() => ({ config }), [config]);

  if (!isLoaded) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          backgroundColor: config.isDark
            ? getThemeColor("dark")
            : getThemeColor("light"),
        }}
      />
    );
  }

  return (
    <AppConfigContext.Provider value={configContextValue}>
      <AppActionsContext.Provider value={actionsContextValue}>
        {children}
      </AppActionsContext.Provider>
    </AppConfigContext.Provider>
  );
};

AppProvider.propTypes = { children: PropTypes.node };

// Backward-compatible hook — zero consumer changes required.
// Spreads both contexts so all existing destructuring patterns work unchanged.
export const useAppContext = () => ({
  ...useContext(AppConfigContext),
  ...useContext(AppActionsContext),
});

export default AppProvider;
